import React from 'react';
import { Link } from 'react-router-dom';
import renderHtml from 'helpers/renderHtml';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import uuid from 'uuid-random';
import { POLICY_LINKS } from 'variables/main';
import { getLanguage } from 'translation';

const TypographyStyled = styled(Typography)(({ theme, variant }) => ({
  marginBottom: 20,
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    ...(variant === 'h2'
      ? {
          fontSize: 22,
          lineHeight: '28px'
        }
      : {}),
    ...(variant === 'h3'
      ? {
          fontSize: 20,
          lineHeight: '26px'
        }
      : {}),
    ...(variant === 'h4'
      ? {
          fontSize: 18,
          lineHeight: '24px'
        }
      : {}),
    ...(variant === 'h5'
      ? {
          fontSize: 16,
          lineHeight: '22px'
        }
      : {}),
    ...(variant === 'h6'
      ? {
          fontSize: 16,
          lineHeight: '22px'
        }
      : {})
  }
}));

const Paragraph = styled(Typography)(({ theme, variant }) => ({
  marginBottom: 20,
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('sm')]: {
    ...(variant === 'h2'
      ? {
          fontSize: 22,
          lineHeight: '28px'
        }
      : {})
  }
}));

const Quote = styled('blockquote')(({ theme }) => ({
  borderLeft: `5px solid ${theme.palette.primary.main}`,
  paddingLeft: 20,
  marginBottom: 20,
  color: theme.palette.textPrimary.main,
  fontFamily: 'Rubik-Regular',
  fontSize: 18,
  fontWeight: 400,
  lineHeight: '27px'
}));

const StyledList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  fontFamily: 'Rubik-Regular',
  color: 'rgba(34, 34, 34, 0.8)',
  position: 'relative',
  marginBottom: 20,
  '& > li': {
    counterIncrement: 'li-count',
    position: 'relative',
    '&::before': {
      content: '" "',
      marginRight: 20,
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      position: 'absolute',
      left: 0,
      top: 8
    }
  },
  '& ul': {
    paddingLeft: 20,
    '& li': {
      '&::before': {
        content: '" "',
        marginRight: 20,
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        position: 'absolute',
        left: 0,
        top: 8
      }
    }
  },
  '& ol': {
    paddingLeft: 20
  },
  '& ol li': {
    counterIncrement: 'li-count',
    '&::before': {
      content: 'counter( li-count)',
      color: theme.palette.primary.main,
      marginRight: 20,
      fontSize: 30,
      lineHeight: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      left: 0,
      minHeight: 'unset'
    }
  }
}));

const StyledListNumbered = styled('ol')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  fontFamily: 'Rubik-Regular',
  color: 'rgba(34, 34, 34, 0.8)',
  counterReset: 'li-count',
  position: 'relative',
  marginBottom: 20,
  '& > li': {
    counterIncrement: 'li-count',
    position: 'relative',
    '&::before': {
      content: 'counter( li-count)',
      color: theme.palette.primary.main,
      marginRight: 20,
      fontSize: 28,
      lineHeight: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      left: 0,
      textAlign: 'center',
      minWidth: 15
    }
  },
  '& ul': {
    paddingLeft: 20,
    '& li': {
      '&::before': {
        content: '" "',
        marginRight: 20,
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        position: 'absolute',
        left: 0,
        top: 8,
        minWidth: 'unset'
      }
    }
  },
  '& ol': {
    paddingLeft: 20,
    '& li': {
      counterIncrement: 'li-count',
      '&::before': {
        content: 'counter( li-count)',
        color: theme.palette.primary.main,
        marginRight: 20,
        fontSize: 28,
        lineHeight: '28px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        left: 0,
        textAlign: 'center',
        minWidth: 15
      }
    }
  }
}));

const ListItem = styled('li')(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  fontFamily: 'Rubik-Regular',
  fontSize: 18,
  fontWeight: 400,
  lineHeight: '27px',
  minHeight: 40,
  paddingLeft: 30
}));

const serialize = (props, component) => {
  const lang = getLanguage();
  const children = props?.root?.children || props;

  try {
    return (
      children?.map((node) => {
        if (typeof node.text === 'string') {
          let text = <span key={uuid()}>{renderHtml(node.text)}</span>;

          if (node.bold || node.format === 1) {
            text = <strong key={uuid()}>{text}</strong>;
          }

          if (node.code) {
            text = <code key={uuid()}>{text}</code>;
          }

          if (node.italic) {
            text = <em key={uuid()}>{text}</em>;
          }

          if (node.underline) {
            text = (
              <span style={{ textDecoration: 'underline' }} key={uuid()}>
                {text}
              </span>
            );
          }

          if (node.strikethrough) {
            text = (
              <span style={{ textDecoration: 'line-through' }} key={uuid()}>
                {text}
              </span>
            );
          }

          return text;
        }

        if (!node) {
          return null;
        }

        const nodeType = node.tag || node.type || node.blockType;

        if (component) {
          return (
            <component key={uuid()} className={nodeType}>
              {serialize(node?.children)}
            </component>
          );
        }

        switch (nodeType) {
          case 'h1':
            return (
              <TypographyStyled variant="h1" key={uuid()}>
                {serialize(node?.children)}
              </TypographyStyled>
            );
          case 'h2':
            return (
              <TypographyStyled variant="h2" key={uuid()}>
                {serialize(node?.children)}
              </TypographyStyled>
            );
          case 'h3':
            return (
              <TypographyStyled variant="h3" key={uuid()}>
                {serialize(node?.children)}
              </TypographyStyled>
            );
          case 'h4':
            return (
              <TypographyStyled variant="h4" key={uuid()}>
                {serialize(node?.children)}
              </TypographyStyled>
            );
          case 'h5':
            return (
              <TypographyStyled variant="h5" key={uuid()}>
                {serialize(node?.children)}
              </TypographyStyled>
            );
          case 'h6':
            return (
              <TypographyStyled variant="h6" key={uuid()}>
                {serialize(node?.children)}
              </TypographyStyled>
            );
          case 'blockquote':
            return <Quote key={uuid()}>{serialize(node?.children)}</Quote>;
          case 'ul':
            return <StyledList key={uuid()}>{serialize(node?.children)}</StyledList>;
          case 'ol':
            return <StyledListNumbered key={uuid()}>{serialize(node.children)}</StyledListNumbered>;
          case 'list':
            switch (node.listType) {
              case 'bullet':
                return <StyledList key={uuid()}>{serialize(node?.children)}</StyledList>;
              case 'ordered':
                return (
                  <StyledListNumbered key={uuid()}>{serialize(node?.children)}</StyledListNumbered>
                );
              default:
                return (
                  <StyledListNumbered key={uuid()}>{serialize(node.children)}</StyledListNumbered>
                );
            }
          case 'li':
            return <ListItem key={uuid()}>{serialize(node.children)}</ListItem>;
          case 'listitem':
            return <ListItem key={uuid()}>{serialize(node.children)}</ListItem>;
          case 'link':
            const url = (() => {
              if (node?.fields?.doc?.value) {
                return POLICY_LINKS[node?.fields?.doc?.value];
              }

              return node.url;
            })();

            return (
              <Link key={uuid()} target={node.linkType === 'external' ? '_blank' : null} to={url}>
                {serialize(node?.children)}
              </Link>
            );
          case 'label':
            return <Paragraph key={uuid()}>{serialize(node?.children)}</Paragraph>;
          case 'large-body': {
            return <Paragraph key={uuid()}>{serialize(node?.children)}</Paragraph>;
          }
          case 'disclaimer':
            return <Paragraph key={uuid()}>{serialize(node?.children)}</Paragraph>;
          case 'paragraph':
            return <Paragraph key={uuid()}>{serialize(node?.children)}</Paragraph>;
          case 'description':
            const largeFont = node.LargeFont[lang];
            const smallFont = node.SmallFont[lang];
            return (
              <>
                <Paragraph key={uuid()}>{largeFont}</Paragraph>
                <Paragraph key={uuid()}>{smallFont}</Paragraph>
              </>
            );
          case 'linebreak': {
            return <br key={uuid()} />;
          }
          default:
            return <Paragraph key={uuid()}>{serialize(node?.children)}</Paragraph>;
        }
      }) || []
    );
  } catch {
    return null;
  }
};

export default serialize;
